import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import clsx from 'clsx';

interface ToastEntry { id: number; kind: 'info' | 'error'; text: string; }

interface ToastContextValue {
  push: (kind: 'info' | 'error', text: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 3;
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const push = useCallback((kind: 'info' | 'error', text: string) => {
    const id = nextId++;
    setItems((cur) => {
      const next = [...cur, { id, kind, text }];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    window.setTimeout(() => setItems((cur) => cur.filter((t) => t.id !== id)), 4000);
  }, []);
  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div
        aria-live="polite"
        role="status"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      >
        {items.map((t) => (
          <div
            key={t.id}
            style={{ animation: 'toast-in var(--duration-fast) var(--ease-out) both' }}
            className={clsx(
              'pointer-events-auto flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2 text-sm shadow-[var(--shadow-2)]',
              t.kind === 'error'
                ? 'border-danger/40 bg-danger/10 text-danger'
                : 'border-border-subtle bg-[var(--color-bg-elevated)] text-text-primary',
            )}
          >
            {t.kind === 'error' ? <CircleAlert className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

export function ToastOnEvent({ when, kind, text }: { when: boolean; kind: 'info' | 'error'; text: string }) {
  const { push } = useToast();
  useEffect(() => { if (when) push(kind, text); }, [when, kind, text, push]);
  return null;
}
